import { reportClientError } from './report-client-error';

// One noisy page (a console.error in a render loop, a broken extension) must not flood the API.
const MAX_REPORTS_PER_PAGE = 20;
const DEDUPE_WINDOW_MS = 60_000;

const stringify = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // Circular structures, BigInt, … — fall back to the default string form.
    return String(value);
  }
};

// console.error accepts anything; keep the original error (ApiError status, stack) when there is
// exactly one, otherwise fold all arguments into one message and borrow the first Error's name/stack.
const fromConsoleArgs = (args: unknown[]): unknown => {
  if (args.length === 1) return args[0];
  const message = args.map(stringify).join(' ');
  const firstError = args.find((a): a is Error => a instanceof Error);
  const combined = new Error(message);
  combined.name = firstError?.name ?? 'ConsoleError';
  combined.stack = firstError?.stack;
  return combined;
};

let installed = false;

/**
 * Forwards every error-level event in the browser to the server logs:
 * `console.error` calls, uncaught exceptions and unhandled promise rejections.
 * Call once at startup. Per page load, identical errors are sent once a minute and at most 20 in total.
 */
export const installErrorForwarding = (): void => {
  if (installed) return;
  installed = true;

  let sent = 0;
  let reporting = false;
  const lastSentAt = new Map<string, number>();

  const forward = (source: string, error: unknown): void => {
    // Guards against anything inside the report path logging an error and re-entering here.
    if (reporting || sent >= MAX_REPORTS_PER_PAGE) return;
    const key = `${source}:${stringify(error)}`;
    const now = Date.now();
    const last = lastSentAt.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
    lastSentAt.set(key, now);
    sent += 1;

    reporting = true;
    try {
      reportClientError(source, error);
    } finally {
      reporting = false;
    }
  };

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    originalConsoleError.apply(console, args);
    forward('console.error', fromConsoleArgs(args));
  };

  window.addEventListener('error', (event) => forward('window.error', event.error ?? event.message));
  window.addEventListener('unhandledrejection', (event) => forward('window.unhandledrejection', event.reason));
};
